export interface ParentUserInfo {
  id: number;
  name: string;
  firstname: string;
  email: string;
  code_parrainage: string;
  raison_social?: string;
  is_entreprise: boolean;
  referralPercentage: number;
}

export interface UserPurchase {
  user: {
    id: number;
    email: string;
    daily_email_quota: number;
    monthly_email_quota: number;
  };
  offer: {
    id: number;
    name: string;
    price: number;
    daily_emails: number;
    monthly_emails: number;
  };
  purchasedAt: string;
}

export interface UserModel {
  avatarUrl: string | undefined;
  id: number;
  reference: string;
  adresse: string;
  postal_code: string;
  country: string;
  number: string;
  password: string;
  email: string;
  user_type: 'personne' | 'entreprise' | 'professionnel';
  raison_social: string | null;
  code_SIREN: string | null;
  code_NAF: string | null;
  civility: string;
  tv_intra: string;
  name: string;
  role: string;
  referral_percentage: number;
  remise: number;
  referralPercentage: number;
  firstname: string;
  createdAt: Date;
  updatedAt: Date;
  code_parrainage: string;
  parent_id: number | null;
  announcementsCount: number;
  referralCount?: number;
  commissionUnpaid?: number;
  clientsAnnouncementsCount?: number;
  entete: string;
  hasWithdrawnThisMonth: boolean;
  lastWithdrawalDate: string | null;
  bank_name: string;
  account_holder: string;
  account_number: string;
  iban: string;
  bic_swift: string;
  is_entreprise: boolean;
  status: string;
  already_connected?: boolean;
  first_login_skipped?: boolean;
  hasFreeAnnouncement?: boolean;
  commitment_accepted: boolean;
  email_sent_number: number;
  usedReferralCode?: string;
  plain_password?: string;
  points_balance?: number;
  totalPointsEarned?: number;
  Parent?: Partial<ParentUserInfo>;
  created_at: string;
  updated_at: string;
  solde?: number;

  // Profession: valeur finale stockée (sélection ou texte libre)
  profession?: string;
  profession_other?: string | null;
  profession_is_other?: boolean;

  // Nouveaux champs pour les professionnels
  kbis_document?: string | null;
  identity_document_recto?: string | null;
  identity_document_verso?: string | null;
  identity_document_type?: 'cin' | 'passeport' | null;
  documents_verified?: boolean;
  documents_verification_date?: Date | null;
  is_validated: boolean;
  rib_document?: string;
  rib?: string | null;
  
  // Champs pour le suivi des envois d'emails
  emails_sent_today: number;
  emails_sent_this_month: number;
  emails_sent_total: number;
  last_email_count_reset: Date | null;
  daily_email_quota: number;
  monthly_email_quota: number;
  oldPoints?: number;
  newPoints?: number;
  unpaid_annonces_count?: number;
  unpaid_amount?: number;
  unpaid_annonce_limit?: number;
  unpaid_amount_limit?: number;
  avoir_balance?: number;
  purchases?: UserPurchase[];
  email_autorized: boolean;
  soldeRemise?: number;
  profile_picture?: string;
  show_in_directory?: boolean;
  rgpd_directory_consent?: boolean;
}

export interface ProfessionalDocuments {
  kbis: {
    path?: string;
    uploaded: boolean;
    verified: boolean;
  };
  identity: {
    type: 'cin' | 'passeport';
    recto?: string;
    verso?: string;
    uploaded: boolean;
    verified: boolean;
  };
  rib: {
    document?: string;      
    account_number?: string; 
    bic_swift?: string;     
    uploaded: boolean;
    coordinates_provided: boolean;
    verified: boolean;
  };
}

export const checkProfessionalDocumentsComplete = (user: UserModel): {
  complete: boolean;
  missing: string[];
} => {
  const missing: string[] = [];

  // Vérifier Kbis
  if (!user.kbis_document) {
    missing.push("Extrait Kbis");
  }

  // Vérifier pièce d'identité
  if (!user.identity_document_recto) {
    missing.push("Pièce d'identité (recto)");
  }

  if (user.identity_document_type === 'cin' && !user.identity_document_verso) {
    missing.push("Pièce d'identité (verso)");
  }

  // ✅ Vérifier document RIB
  if (!user.rib_document) {
    missing.push("Document RIB");
  }

  // ✅ Vérifier coordonnées bancaires
  if (!user.account_number) {
    missing.push("RIB (coordonnées)");
  }

  if (!user.bic_swift) {
    missing.push("BIC/SWIFT");
  }

  return {
    complete: missing.length === 0,
    missing
  };
};

export const formatRIB = (rib?: string): string => {
  if (!rib) return "-";
  const clean = rib.replace(/\s/g, '');
  if (clean.length !== 23) return rib;
  
  const bank = clean.slice(0, 5);
  const guichet = clean.slice(5, 10);
  const compte = clean.slice(10, 21);
  const cle = clean.slice(21);
  
  return `${bank} ${guichet} ${compte} ${cle}`;
};

export const getDocumentStatus = (user: UserModel): {
  status: 'complete' | 'incomplete' | 'pending';
  message: string;
  color: string;
} => {
  if (user.user_type !== 'professionnel') {
    return {
      status: 'complete',
      message: 'Non applicable',
      color: '#6c757d'
    };
  }

  const check = checkProfessionalDocumentsComplete(user);

  if (check.complete && user.documents_verified) {
    return {
      status: 'complete',
      message: 'Documents complets et vérifiés',
      color: '#28a745'
    };
  }

  if (check.complete && !user.documents_verified) {
    return {
      status: 'pending',
      message: 'Documents complets - En attente de vérification',
      color: '#ffc107'
    };
  }

  return {
    status: 'incomplete',
    message: `Documents manquants : ${check.missing.join(', ')}`,
    color: '#dc3545'
  };
};



export class ConvertUser {
  public static toUserModel(json: string): UserModel {
    const data = JSON.parse(json);
    return {
      ...data,
      announcementsCount: data.announcementsCount || 0,
      referralCount: data.referralCount || 0,
      commissionUnpaid: data.commissionUnpaid || 0,
      clientsAnnouncementsCount: data.clientsAnnouncementsCount || 0,
      usedReferralCode: data.usedReferralCode || data.used_referral_code || undefined,
      points_balance: data.points_balance ?? 0,
      totalPointsEarned: data.totalPointsEarned ?? 0,
      Parent: data.Parent || undefined,
      profession: data.profession || data.profession_other || undefined,
      profession_other: data.profession_other || undefined,
      profession_is_other: data.profession_is_other ?? false,
      kbis_document: data.kbis_document || null,
      identity_document_recto: data.identity_document_recto || null,
      identity_document_verso: data.identity_document_verso || null,
      identity_document_type: data.identity_document_type || null,
      documents_verified: data.documents_verified ?? false,
      documents_verification_date: data.documents_verification_date || null,
      purchases: data.purchases || [],
    };
  }

  public static userModelToJson(value: UserModel): string {
    return JSON.stringify(value);
  }
}